import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
// import { Helmet } from 'react-helmet-async'; // Using SEOHelmet instead
import SEOHelmet from '../../components/SEOHelmet';
import { BreadcrumbSchema } from '../../components/SchemaMarkup';
import { mainPageSeo } from '../../data/seoData';
import { FaMapMarkerAlt, FaDollarSign, FaArrowRight, FaStar } from 'react-icons/fa';
import { hotels } from '../../data/hotels';
import './Hotels.css';

const Hotels = () => {
  const [hotelImages, setHotelImages] = useState({});
  const hotelsSeo = mainPageSeo.hotels;

  // Load custom hotel images from backend API and localStorage
  useEffect(() => {
    const loadImages = async () => {
      try {
        // First, try to load from backend API
        try {
          const response = await fetch('/api/hotel-images');
          if (response.ok) {
            const serverImages = await response.json();
            console.log('✓ Loaded hotel images from backend API:', serverImages);
            // Use actual image URLs directly (Cloudinary URLs work with CORS)
            const imageUrls = {};
            for (const [hotelId, imageUrl] of Object.entries(serverImages)) {
              // For Cloudinary, use directly; for Google Drive, use proxy endpoint
              if (imageUrl.includes('cloudinary.com')) {
                imageUrls[hotelId] = imageUrl;
              } else {
                imageUrls[hotelId] = `/api/proxy-image?url=${encodeURIComponent(imageUrl)}`;
              }
            }
            setHotelImages(imageUrls);
            console.log('✓ Image URLs set:', imageUrls);
            return;
          }
        } catch (apiErr) {
          console.log('Backend API not available, falling back to localStorage');
        }
        
        // Fallback to localStorage
        const savedImages = JSON.parse(localStorage.getItem('hotelImages') || '{}');
        console.log('✓ Loaded hotel images from localStorage:', savedImages);
        // Wrap Google Drive URLs with proxy
        const proxiedImages = {};
        for (const [hotelId, imageUrl] of Object.entries(savedImages)) {
          if (imageUrl.includes('drive.google.com')) {
            proxiedImages[hotelId] = `/api/proxy-image?url=${encodeURIComponent(imageUrl)}`;
          } else {
            proxiedImages[hotelId] = imageUrl;
          }
        }
        setHotelImages(proxiedImages);
      } catch (err) {
        console.error('Error loading hotel images:', err);
      }
    };

    loadImages();

    // Listen for storage changes (when images are saved from admin panel)
    window.addEventListener('storage', loadImages);
    
    return () => {
      window.removeEventListener('storage', loadImages);
    };
  }, []);

  return (
      <div className="hotels-page">
        <SEOHelmet 
          title={hotelsSeo.title}
          description={hotelsSeo.description}
          keywords={hotelsSeo.keywords}
          image={hotelsSeo.image}
          url="https://safarnamah.in/hotels"
          type="website"
        />
        <BreadcrumbSchema items={[
          { name: 'Home', url: 'https://safarnamah.in' },
          { name: 'Hotels', url: 'https://safarnamah.in/hotels' }
        ]} />

        {/* Hero Section */}
        <section className="hotels-hero">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>Boutique Hotels & Retreats</h1>
              <p>
                Handpicked stays emphasizing design, heritage, sustainability and 
                authentic local immersion across Southeast Asia
              </p>
            </motion.div>
          </div>
        </section>

      {/* Hotels Content */}
      <section className="hotels-content">
        <div className="container">
          <div className="hotels-grid">
            {hotels.map((hotel, index) => (
              <motion.div
                key={hotel.id}
                className="hotel-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true, amount: 0.2 }}
              >
                <Link to={`/hotels/${hotel.id}`} className="hotel-card-link">
                  <div className="hotel-image">
                    <img 
                      src={hotelImages[hotel.id] || hotel.image} 
                      alt={hotel.name}
                      style={{ width: '100%', height: '250px', objectFit: 'cover' }}
                      onError={(e) => {
                        // Fallback to default image if custom image fails to load
                        if (e.currentTarget.src !== hotel.image) {
                          e.currentTarget.src = hotel.image;
                        }
                      }}
                    />
                    <div className="hotel-style-badge">{hotel.style}</div>
                  </div>
                  <div className="hotel-content-card">
                    <div className="hotel-meta">
                      <span className="hotel-price">
                        <FaDollarSign /> {hotel.priceRange}
                      </span>
                      <span className="hotel-rating-badge">
                        <FaStar /> {hotel.rating}
                      </span>
                    </div>
                    <h2>{hotel.name}</h2>
                    <p className="hotel-location-text">
                      <FaMapMarkerAlt /> {hotel.location}
                    </p>
                    <p>{hotel.description}</p>
                    <div className="hotel-footer">
                      <div className="hotel-info">
                        <span className="hotel-type">
                          Boutique Hotel
                        </span>
                      </div>
                      <span className="view-details">
                        View Details <FaArrowRight />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Hotels;
